export type Metric = 'credits' | 'usd';

export interface ChartDatum {
  name: string;
  [series: string]: number | string;
}

/**
 * Sums rows that share a label into chart bars and keeps the `limit` biggest
 * by the total of the series. `series` maps a series key to a getter, so the
 * same helper feeds a single-bar chart and a stacked one.
 */
export function topBars<T>(
  rows: readonly T[],
  label: (row: T) => string,
  series: Record<string, (row: T) => number>,
  limit = 15,
): ChartDatum[] {
  const byName = new Map<string, ChartDatum>();
  for (const row of rows) {
    const name = label(row);
    let datum = byName.get(name);
    if (!datum) {
      datum = { name, ...Object.fromEntries(Object.keys(series).map((k) => [k, 0])) };
      byName.set(name, datum);
    }
    for (const [key, get] of Object.entries(series)) {
      datum[key] = (datum[key] as number) + get(row);
    }
  }
  const total = (d: ChartDatum) => Object.keys(series).reduce((s, k) => s + (d[k] as number), 0);
  return [...byName.values()].sort((a, b) => total(b) - total(a)).slice(0, limit);
}

export function userLabel(row: { email?: string | null; name?: string | null; profile_id?: string | null }): string {
  return row.email || row.name || row.profile_id || 'unknown';
}

type Num = number | null | undefined;

const EMPTY = '—';

/** Credits: thousands separators, at most one decimal. Unknown (null) is a dash, never 0. */
export function formatCredits(n: Num): string {
  if (n === null || n === undefined || Number.isNaN(n)) return EMPTY;
  return n.toLocaleString('en-US', { maximumFractionDigits: 1 });
}

/**
 * USD, one style everywhere: 2 decimals from $1 up, 4 decimals below $1, so a
 * column of small costs lines up ($0.4226 next to $0.0003). Unknown is a dash.
 */
export function formatUsd(n: Num): string {
  if (n === null || n === undefined || Number.isNaN(n)) return EMPTY;
  if (Math.abs(n) >= 1) {
    return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  if (n !== 0 && Math.abs(n) < 0.00005) return n < 0 ? '-<$0.0001' : '<$0.0001';
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
}

/** The same style for chart axis ticks, without trailing zeros ($0.03, not $0.0300). */
export function formatUsdTick(n: number): string {
  const digits = Math.abs(n) >= 1 ? 2 : 4;
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: digits })}`;
}

export function formatInt(n: Num): string {
  if (n === null || n === undefined || Number.isNaN(n)) return EMPTY;
  return Math.round(n).toLocaleString('en-US');
}

/**
 * Shortens a label to `max` characters with the ellipsis in the middle, so the
 * meaningful tail survives: "openrouter/google/gemini-2.5-flash" becomes
 * "openrouter/…/gemini-2.5-flash", a long email keeps its domain.
 */
export function ellipsizeMiddle(label: string, max: number): string {
  if (label.length <= max) return label;
  const parts = label.split('/');
  if (parts.length > 2) {
    const short = `${parts[0]}/…/${parts[parts.length - 1]}`;
    if (short.length <= max) return short;
  }
  const keep = Math.max(2, max - 1);
  const tail = Math.ceil(keep * 0.65);
  return `${label.slice(0, keep - tail)}…${label.slice(label.length - tail)}`;
}

/**
 * "Showing the top 50 of 101 people": what a response left out, or null when it
 * left nothing out. `noun` is what `total` counts (people for the by-user
 * reports, rows for the rest).
 */
export function truncationLabel(
  meta: { total?: number; shown?: number; truncated?: boolean; sort?: Metric },
  noun: string,
): string | null {
  if (!meta.truncated) return null;
  const by = meta.sort === 'credits' ? 'credits' : 'USD';
  const of = meta.total !== undefined && meta.shown !== undefined && meta.total > meta.shown
    ? `Showing the top ${meta.shown} of ${meta.total} ${noun}, ranked by ${by}.`
    : `Showing the largest ${noun} by ${by}; the smallest are left out.`;
  return `${of} The chart and the CSV cover these rows only.`;
}

/** A CSV name that says it is a cut: "…_top-50-of-101.csv"; unchanged when nothing was left out. */
export function csvNameFor(
  name: string,
  meta: { total?: number; shown?: number; truncated?: boolean },
): string {
  if (!meta.truncated || meta.total === undefined || meta.shown === undefined) return name;
  return name.replace(/\.csv$/, `_top-${meta.shown}-of-${meta.total}.csv`);
}
